import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { getUserFullPermissions } from "../services/permissionService";
import { getUserId } from "../comman/function";

export interface Permission {
  permissionId?: number;
  permissionKey: string;
  [key: string]: unknown;
}

export interface QueueAssignment {
  workflowType: string;
  queueName: string;
  [key: string]: unknown;
}

export interface PermissionContextValue {
  permissions: Permission[];
  permissionKeys: string[];
  queueAssignments: QueueAssignment[];
  queueNames: string[];
  loading: boolean;
  error: string | null;
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeyList: string[]) => boolean;
  hasAllPermissions: (permissionKeyList: string[]) => boolean;
  isAssignedToQueue: (queueName: string) => boolean;
  isAssignedToAnyQueue: (queueNameList: string[]) => boolean;
  getQueuesForWorkflow: (workflowType: string) => QueueAssignment[];
  refreshPermissions: () => Promise<void> | void;
  clearPermissions: () => void;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

/**
 * Permission Provider
 * Provides permission state and helper functions to the entire app
 */
export const PermissionProvider = ({ children }: PermissionProviderProps) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionKeys, setPermissionKeys] = useState<string[]>([]);
  const [queueAssignments, setQueueAssignments] = useState<QueueAssignment[]>([]);
  const [queueNames, setQueueNames] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load user's permissions from API with retry logic
   */
  const loadUserPermissions = useCallback(async (retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second

    try {
      setLoading(true);
      setError(null);
      const userId = getUserId();

      if (!userId) {
        // User not logged in, clear permissions
        setPermissions([]);
        setPermissionKeys([]);
        setQueueAssignments([]);
        setQueueNames([]);
        setLoading(false);
        return;
      }

      interface FullPermissionsResponse {
        status: string;
        data?: {
          permissions?: Permission[];
          permissionKeys?: string[];
          queueAssignments?: QueueAssignment[];
          queueNames?: string[];
        };
      }
      const response = (await getUserFullPermissions(userId)) as FullPermissionsResponse;

      if (response.status === "Success" && response.data) {
        setPermissions(response.data.permissions || []);
        setPermissionKeys(response.data.permissionKeys || []);
        setQueueAssignments(response.data.queueAssignments || []);
        setQueueNames(response.data.queueNames || []);
      } else {
        setError("Failed to load permissions");
      }
    } catch (err) {
      console.error("[PermissionContext] Error loading permissions:", err);

      // Retry on failure (handles transient connection errors)
      if (retryCount < MAX_RETRIES) {
        console.log(
          `[PermissionContext] Retrying in ${RETRY_DELAY}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`
        );
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return loadUserPermissions(retryCount + 1);
      }

      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load permissions on mount and when userId changes
   */
  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      loadUserPermissions();
    } else {
      setLoading(false);
    }
  }, [loadUserPermissions]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      return permissionKeys.includes(permissionKey);
    },
    [permissionKeys]
  );

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (permissionKeyList: string[]): boolean => {
      if (!Array.isArray(permissionKeyList)) return false;
      return permissionKeyList.some((key) => permissionKeys.includes(key));
    },
    [permissionKeys]
  );

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (permissionKeyList: string[]): boolean => {
      if (!Array.isArray(permissionKeyList)) return false;
      return permissionKeyList.every((key) => permissionKeys.includes(key));
    },
    [permissionKeys]
  );

  /**
   * Check if user is assigned to a specific queue
   */
  const isAssignedToQueue = useCallback(
    (queueName: string): boolean => {
      return queueNames.includes(queueName);
    },
    [queueNames]
  );

  /**
   * Check if user is assigned to ANY of the specified queues
   */
  const isAssignedToAnyQueue = useCallback(
    (queueNameList: string[]): boolean => {
      if (!Array.isArray(queueNameList)) return false;
      return queueNameList.some((name) => queueNames.includes(name));
    },
    [queueNames]
  );

  /**
   * Get queues for a specific workflow type
   */
  const getQueuesForWorkflow = useCallback(
    (workflowType: string): QueueAssignment[] => {
      return queueAssignments.filter((q) => q.workflowType === workflowType);
    },
    [queueAssignments]
  );

  /**
   * Refresh permissions from API
   */
  const refreshPermissions = useCallback((): Promise<void> => {
    return loadUserPermissions();
  }, [loadUserPermissions]);

  /**
   * Clear permissions (e.g., on logout)
   */
  const clearPermissions = useCallback((): void => {
    setPermissions([]);
    setPermissionKeys([]);
    setQueueAssignments([]);
    setQueueNames([]);
  }, []);

  const value: PermissionContextValue = {
    // State
    permissions,
    permissionKeys,
    queueAssignments,
    queueNames,
    loading,
    error,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Queue checks
    isAssignedToQueue,
    isAssignedToAnyQueue,
    getQueuesForWorkflow,

    // Actions
    refreshPermissions,
    clearPermissions,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

/**
 * Hook to access permission context
 */
export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};

/**
 * Permission constants for easy reference
 */
export const PERMISSIONS = {
  // Invoice
  INVOICE_VIEW_ASSIGNED: "invoice.view.assigned",
  INVOICE_VIEW_ALL: "invoice.view.all",
  INVOICE_UPLOAD: "invoice.upload",
  INVOICE_EDIT: "invoice.edit",
  INVOICE_APPROVE: "invoice.approve",
  INVOICE_REJECT: "invoice.reject",
  INVOICE_LOCK: "invoice.lock",
  INVOICE_DELETE: "invoice.delete",
  INVOICE_EXPORT: "invoice.export",

  // Exception
  EXCEPTION_REQUEST_INFO: "exception.handle.request_info",
  EXCEPTION_SUPPLIER: "exception.handle.supplier",
  EXCEPTION_SPECIALIST: "exception.handle.specialist",
  EXCEPTION_REJECTION: "exception.handle.rejection",
  EXCEPTION_DUPLICATE: "exception.handle.duplicate",

  // Workflow
  WORKFLOW_CENTRAL_RECEIVING: "workflow.central_receiving.access",
  WORKFLOW_DIRECT_PAY: "workflow.direct_pay.access",
  WORKFLOW_FACILITIES: "workflow.facilities.access",
  WORKFLOW_MY_APPROVALS: "workflow.my_approvals.access",
  WORKFLOW_SEND_BACK: "workflow.send_back",

  // Admin
  ADMIN_USERS_VIEW: "admin.users.view",
  ADMIN_USERS_MANAGE: "admin.users.manage",
  ADMIN_USERS_PERMISSIONS: "admin.users.permissions",
  ADMIN_SETTINGS_VIEW: "admin.settings.view",
  ADMIN_SETTINGS_MANAGE: "admin.settings.manage",
  ADMIN_WORKFLOW_VIEW: "admin.workflow.view",
  ADMIN_WORKFLOW_MANAGE: "admin.workflow.manage",
  ADMIN_SUPPLIERS_VIEW: "admin.suppliers.view",
  ADMIN_SUPPLIERS_MANAGE: "admin.suppliers.manage",
  ADMIN_REPORTS_VIEW: "admin.reports.view",
  ADMIN_REPORTS_EXPORT: "admin.reports.export",
  ADMIN_GLCODES_MANAGE: "admin.glcodes.manage",
  ADMIN_AUDIT_VIEW: "admin.audit.view",
  ADMIN_SSO_VIEW: "admin.sso.view",
  ADMIN_SSO_MANAGE: "admin.sso.manage",
} as const;

export default PermissionContext;
